import { type Property } from 'csstype';

import { theme } from '~/themes/default/theme';

export const fontFamily =
  "neue-haas-unica, pragmatica-web, 'Helvetica Neue', Helvetica, Arial, sans-serif";

// Feron V3 color update
// Palette comes from
// https://www.figma.com/file/8hviGiukYiE00M3DZCQSUl/Shippo-Brand-Colors-3.0-Final?

const v3OldColors = {
  gray10: '#F7FBFC',
  gray20: '#E9F1F5',

  gray30: '#CFDEE6',

  gray40: '#B8CED9',

  gray50: '#7B99A8',
  gray60: '#5C7B8A',
  gray70: '#4C6774',
  gray80: '#425B66',
  gray90: '#2E434D',
  gray100: '#1F2D34',
  orchid70: '#A100C1',
  royalOrange70: '#CA4F02',
  text: {
    primary: {
      black: '#1B1D1D',
      blue: '#00AFF3', /// cyan 50
      green: '#A0D249', /// northstar 50
      white: '#FFFFFF',
    },
  },
  white: '#FFFFFF',
};

// Scaled Colors
// Shippo Design System 2.2020
// https://www.figma.com/file/6bmZ29sLXCplMNzaZ2z7O7/Shippo-Design-System?node-id=68%3A0
const designSystemColors = {
  black: '#1B1D1D',

  forest50: theme.palette.success.light,
  forest100: theme.palette.success.light,
  forest200: theme.palette.success.light,
  forest600: theme.palette.success.main,
  forest700: theme.palette.success.main,
  forest800: theme.palette.primary.dark,

  gray10: v3OldColors.gray10,
  gray25: v3OldColors.gray20,
  gray50: v3OldColors.gray20,
  gray100: v3OldColors.gray10,
  gray200: v3OldColors.gray20,
  gray300: v3OldColors.gray30,
  gray400: v3OldColors.gray40,
  gray500: v3OldColors.gray50,
  gray600: v3OldColors.gray60,
  gray700: v3OldColors.gray70,
  gray800: v3OldColors.gray80,
  gray900: v3OldColors.gray90,
  gray1000: v3OldColors.gray100,

  macaroni50: theme.palette.warning.light,
  macaroni100: theme.palette.warning.light,
  macaroni300: theme.palette.warning.light,
  macaroni700: theme.palette.warning.main,
  macaroni800: theme.palette.warning.main,
  macaroni900: theme.palette.warning.main,

  ruby600: theme.palette.error.main,
  ruby700: theme.palette.error.main,

  sea25: theme.palette.secondary.hover,
  sea50: theme.palette.secondary.dark,
  sea300: theme.palette.secondary.main,
  sea600: theme.palette.secondary.main,
  sea700: theme.palette.secondary.main,

  white: '#FFFFFF',
};

export const colors = {
  ...designSystemColors,

  almostWhite: '#F1F5F7',
  aqua: '#31A2C4',

  branding: {
    defaultBrandPrimaryColor: '#000000',
    defaultBrandSecondaryColor: '#F1F1F1',
  },
  containerBorderColor: theme.borderColors.default, // the container or section separations should use this color as border.
  dangerRed: theme.palette.error.main,
  dialog: {
    errorBackground: theme.palette.error.light,
    infoBackground: theme.palette.secondary.hover,
    successBackground: theme.palette.success.light,
  },

  disabledIcon: '#e3e4e5',
  emperor: '#555555', // Same as form.labelFontColor, ex matterhornGray

  fontColor: '#333333',
  form: {
    activeBorderColor: theme.borderColors.default,
    disabledLabelFontColor: theme.borderColors.default,
    errorBackground: theme.palette.error.light,
    errorBar: theme.palette.error.main,
    errorBorder: theme.palette.error.main,
    fontColor: theme.palette.text.primary,
    greyBorder: theme.borderColors.default,
    greyDisabled: theme.palette.secondary.hover!,
    greyDisabledBorder: theme.palette.secondary.dark,
    greyDisabledButton: theme.palette.secondary.dark,
    labelFontColor: designSystemColors.black,
    placeholderFontColor: theme.borderColors.default,
    shadow: theme.borderColors.default,
    successBackground: designSystemColors.gray25,
    successBar: theme.palette.success.light,
    successBorder: theme.palette.success.light,
  },

  /**
   * 2020 Colors (not from new design system)
   * Temporarily using this to name colors until we use the des. sys. color gradient naming convention
   * http://chir.ag/projects/name-that-color
   */
  geyser: '#cfdce2',
  greyBorder: theme.borderColors.default,
  loblolly: theme.borderColors.default, // Same as tags.highlighted.borderColor, also used in left nav
  nightRider: '#333333', // Ex mineShaft2, same as form.fontColor
  notification: {
    boxShadow: 'rgba(0, 0, 0, 0.12)',
  },
  // TODO: Pagination colors, not final
  pagination: {
    activeBorder: theme.palette.success.main,
    borderColor: theme.borderColors.default,
    hoverBackground: theme.palette.secondary.dark,
  },

  placeholderColor: '#AEAEAE',
  primary: theme.palette.primary.main,
  primaryAlternative: theme.palette.primary.dark,
  primaryFocus: theme.palette.primary.dark,
  rollingStone: '#718087',
  secondary: theme.palette.secondary.main,
};

export const formSizes = {
  inputHeight: '2.5rem',
  inputWidth: '15rem',
};

export const zIndexValues = {
  anchoredDiv: 98,
  banner: 1000,
  base: 1,
  dropdown: 15,
  leftColumn: 2,
  modal: 101,
  /**
   * Above MUI Drawer, but below MUI Dialog.
   *
   * Used when MUI dialogs are opened from within a modal, so that the dialog can stack on top of the modal.
   */
  modalAboveMuiDrawer: 1250,
  multiSelect: 100,
  orderBatchMenu: 10,
  overlay: 99,
  persistantBanner: 999,
  tooltip: 1070,
};

export const fontWeight: Record<string, Property.FontWeight> = {
  black: 900,
  bold: 700,
  extraBold: 800,
  extraLight: 200,
  light: 300,
  medium: 500,
  normal: 400,
  semiBold: 600,
  thin: 100,
};
